import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to set up Gatsby and Auth0 for easy authentication on your next project",
  "description": "",
  "published": "2019-01-23T00:00:00.000Z",
  "redirect_from": ["/blog/gatsby-auth0/swizec/8895"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`In my quest never to set up a server ever again, I discovered Auth0 – a serverless authentication service. You give them keys to the kingdom, and they keep you and your users secure.`}</p>
    <p>{`You don't even have to store anything. 👌`}</p>
    <p>{`Since Gatsby is my favorite way to build new React apps with server-side rendering and super-fast performance, I decided to combine the two. Easy in theory, but turns out tricky in practice.`}</p>
    <p>{`Took me 3 livecoding sessions to figure out. `}<a parentName="p" {...{
        "href": "https://auth0.com/blog/building-a-blog-with-gatsby-react-and-webtask/"
      }}>{`Luke Oliff's article on Gatsby v1 + Auth0`}</a>{` article helped a lot, as did `}<a parentName="p" {...{
        "href": "https://auth0.com/docs/quickstart/spa/react/01-login"
      }}>{`Auth0's React SPA setup guide`}</a>{`.`}</p>
    <p>{`You can think of this article as a distillation of those guides and these 3 hours of coding 👇`}</p>
    <lite-youtube {...{
      "videoid": "xwyOQqi7Xm8",
      "videostartat": "0"
    }}></lite-youtube>
    <lite-youtube {...{
      "videoid": "jF0AIV8EtJk",
      "videostartat": "0"
    }}></lite-youtube>
    <lite-youtube {...{
      "videoid": "eWPpTIb98YM",
      "videostartat": "0"
    }}></lite-youtube>
    <h2 {...{
      "id": "step-1-get-an-auth0-account"
    }}>{`Step 1: Get an Auth0 account`}</h2>
    <p>{`This one's easy. Go to `}<a parentName="p" {...{
        "href": "https://www.auth0.com"
      }}>{`Auth0`}</a>{` sign up for a free account. They even have a "Just playing around" option, and you're set.`}</p>
    <p>{`Right now, a free account will hold you over up to 7,000 active users. I don't know about you, but none of `}<em parentName="p">{`my`}</em>{` side projects ever hit 7000 DAU (daily active users).`}</p>
    <p>{`See, it's just me on this one`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2019/01/dashboard-screenshot.png",
        "alt": "Auth0 dashboard screenshot"
      }}></img></p>
    <h2 {...{
      "id": "step-2-create-an-app-get-domain-and-client-id"
    }}>{`Step 2: Create an app, get domain and client id`}</h2>
    <p>{`Click the big fat New Application button, fill out the form, and get your application's domain and client id. You'll need these to configure your Auth0 client.`}</p>
    <p>{`The domain is where your login form will live. Your app is going to redirect people there. They'll see a login screen, do the things, and come back to your site with a redirect and a bunch of params.`}</p>
    <p>{`Client ID tells the Auth0 machinery which site it's working with.`}</p>
    <p>{`Strangely, you never have to set up a client secret. They generate one for you, but I haven't needed to use it yet. Maybe when you're doing server-to-server authentication?`}</p>
    <h2 {...{
      "id": "step-3-whitelist-your-callback-url"
    }}>{`Step 3: Whitelist your callback URL`}</h2>
    <p>{`Like I said, Auth0 redirects users back to your site after login. You give it a URL, and that's where they end up.`}</p>
    <p>{`For your safety, callback URLs must be whitelisted. There's an `}<inlineCode parentName="p">{`Allowed Callback URLs`}</inlineCode>{` field in your app settings.`}</p>
    <p>{`Add `}<inlineCode parentName="p">{`http://localhost:8000/auth0_callback`}</inlineCode>{`. I like to prefix callbacks with a service name because you never know how many you'll need.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2019/01/callback-screenshot-1.png",
        "alt": "Callback config"
      }}></img></p>
    <h2 {...{
      "id": "step-4-start-a-new-gatsby-project"
    }}>{`Step 4: Start a new Gatsby project`}</h2>
    <p>{`This part is easy.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ gatsby new gatsby-auth0-playground
`}</code></pre>
    <p>{`Creates a new `}<a parentName="p" {...{
        "href": "https://gatsbyjs.org"
      }}>{`Gatsby`}</a>{` site with the basic setup. You're welcome to use a starter. This guide should work with all of them.`}</p>
    <h2 {...{
      "id": "step-5-the-auth-service"
    }}>{`Step 5: The auth service`}</h2>
    <p>{`All your Auth0 stuff lives in `}<inlineCode parentName="p">{`/utils/auth.js`}</inlineCode>{`. It's like a library on top of Auth0's library that makes your life a little easier.`}</p>
    <p>{`You're best off copy-pasting this whole file verbatim into your project. Make sure to configure `}<inlineCode parentName="p">{`AUTH0_DOMAIN`}</inlineCode>{` and `}<inlineCode parentName="p">{`AUTH0_CLIENT_ID`}</inlineCode>{`. If I was a particularly good human, I'd put this on NPM and make it configurable through package.json, but we're not there yet :P`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Today I learned that in programming when you're stuck, you should copy the instructions. No, not follow the instructions, copypasta their code word for word.`}<br parentName="p"></br><br parentName="p"></br>{`Because copypasta will get you out of the weirdest shit imaginable. `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/200wordsTIL?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#200wordsTIL`}</a><a parentName="p" {...{
          "href": "https://t.co/V3wwtygBiy"
        }}>{`https://t.co/V3wwtygBiy`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1087224394257031168"
      }}>{`January 21, 2019`}</a></blockquote>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/utils/auth.js

import auth0 from 'auth0-js'
import { navigate } from 'gatsby'

const AUTH0_DOMAIN = '<your domain="">.auth0.com'
const AUTH0_CLIENT_ID = '<your client="" id="">'

export default class Auth {
  auth0 = new auth0.WebAuth({
    domain: AUTH0_DOMAIN,
    clientID: AUTH0_CLIENT_ID,
    redirectUri: 'http://localhost:8000/auth0_callback',
    audience: \`https://\${AUTH0_DOMAIN}/api/v2/\`,
    responseType: 'token id_token',
    scope: 'openid profile email',
  })

  login = () => {
    this.auth0.authorize()
  }

  logout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('id_token')
    localStorage.removeItem('expires_at')
    localStorage.removeItem('user')
  }

  handleAuthentication = () => {
    if (typeof window !== 'undefined') {
      // this must've been the trick
      this.auth0.parseHash((err, authResult) => {
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.setSession(authResult)
        } else if (err) {
          console.log(err)
        }

        // Return to the homepage after authentication.
        navigate('/')
      })
    }
  }

  isAuthenticated = () => {
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'))
    return new Date().getTime() < expiresAt
  }

  setSession = authResult => {
    const expiresAt = JSON.stringify(
      authResult.expiresIn * 1000 + new Date().getTime()
    )
    localStorage.setItem('access_token', authResult.accessToken)
    localStorage.setItem('id_token', authResult.idToken)
    localStorage.setItem('expires_at', expiresAt)

    this.auth0.client.userInfo(authResult.accessToken, (err, user) => {
      localStorage.setItem('user', JSON.stringify(user))
    })
  }

  getUser = () => {
    if (localStorage.getItem('user')) {
      return JSON.parse(localStorage.getItem('user'))
    }
  }

  getUserName = () => {
    if (this.getUser()) {
      return this.getUser().name
    }
  }
}
</your></your>
`}</code></pre>
    <p>{`You don't have to understand how all this works, which is the beauty of using a service like Auth0, so here's a nutshell explanation:`}</p>
    <ol>
      <li parentName="ol">{`You set up an `}<inlineCode parentName="li">{`auth0`}</inlineCode>{` client with config, put it in `}<inlineCode parentName="li">{`this.auth0`}</inlineCode></li>
      <li parentName="ol">{`When you call `}<inlineCode parentName="li">{`auth.login()`}</inlineCode>{`, the client takes over`}</li>
      <li parentName="ol">{`Redirects to `}<inlineCode parentName="li">{`AUTH0_DOMAIN`}</inlineCode>{` with some params`}</li>
      <li parentName="ol">{`Users login through Auth0's beautiful UI`}</li>
      <li parentName="ol">{`Auth0 redirects back to `}<inlineCode parentName="li">{`redirectUri`}</inlineCode></li>
      <li parentName="ol">{`Your callback page calls `}<inlineCode parentName="li">{`auth.handleAuthentication`}</inlineCode></li>
      <li parentName="ol">{`Your `}<inlineCode parentName="li">{`auth0`}</inlineCode>{` client parses URL params and decides all's good`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`setSession`}</inlineCode>{` takes auth result and updates info in LocalStorage`}</li>
      <li parentName="ol">{`Gatsby navigates back to your home page with `}<inlineCode parentName="li">{`navigate('/')`}</inlineCode></li>
    </ol>
    <p>{`We also have a couple convenience methods like `}<inlineCode parentName="p">{`isAuthenticated`}</inlineCode>{`, `}<inlineCode parentName="p">{`getUser`}</inlineCode>{`, and `}<inlineCode parentName="p">{`getUserName`}</inlineCode>{`. You might want to make these into proper ES6 getters. I haven't decided yet if that's better or not but it sure looks neater.`}</p>
    <h2 {...{
      "id": "step-6-gatsby-callback-page"
    }}>{`Step 6: Gatsby callback page`}</h2>
    <p>{`I mentioned a "callback page". You might think that's complicated, but with Gatsby, it's not. You just need a React component that loads on `}<inlineCode parentName="p">{`/auth0_callback`}</inlineCode>{` and calls `}<inlineCode parentName="p">{`handleAuthentication`}</inlineCode>{` on mount.`}</p>
    <p>{`Like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/pages/auth0_callback.js

import React from "react";
import { ClipLoader } from "react-spinners";

import Auth from "../utils/auth";
import Layout from "../components/layout";
import useComponentDidMount from "../useComponentDidMount";

const Auth0CallbackPage = () => {
  useComponentDidMount(() => {
    const auth = new Auth();
    auth.handleAuthentication();
  });

  return (
    <layout>
      <h1>
        This is the auth callback page, you should be redirected immediately.
      </h1>
      <cliploader sizeunit="px" size={150}></cliploader>
    </layout>
  );
};

export default Auth0CallbackPage;
`}</code></pre>
    <p>{`I'm using React hooks, but don't let that distract you.`}</p>
    <p>{`Gatsby turns every file in the `}<inlineCode parentName="p">{`/src/pages`}</inlineCode>{` directory into a static page. So you now have a page that renders some text and a loading spinner from `}<inlineCode parentName="p">{`react-spinners`}</inlineCode>{`.`}</p>
    <p>{`When the component mounts, that's only in browsers, we create a new `}<inlineCode parentName="p">{`Auth()`}</inlineCode>{` object and call `}<inlineCode parentName="p">{`auth.handleAuthentication()`}</inlineCode>{`. Our auth service takes care of the rest.`}</p>
    <p>{`PS: the `}<inlineCode parentName="p">{`useComponentDidMount`}</inlineCode>{` hook looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`export default function useComponentDidMount(onMounted) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);

    onMounted();
  }, [mounted]);
}
`}</code></pre>
    <p>{`It's a combination of `}<inlineCode parentName="p">{`useState`}</inlineCode>{` and `}<inlineCode parentName="p">{`useEffect`}</inlineCode>{` that ensures a function runs once and only once – on component mount. My understanding is that `}<inlineCode parentName="p">{`useEffect`}</inlineCode>{` runs any time React touches our component and I wanted to make certain the auth code runs once.`}</p>
    <p>{`If you run `}<inlineCode parentName="p">{`handleAuthentication`}</inlineCode>{` multiple times, it leads to strange errors. State gets corrupted, tokens are out of date, everything breaks.`}</p>
    <h2 {...{
      "id": "step-8-a-login-button"
    }}>{`Step 8: A login button`}</h2>
    <p>{`Now all you need is a login button.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Login.js

import React from "react";
import { Button } from "reakit";
import Auth from "../utils/Auth";

const auth = new Auth();

const Login = () => {
  const { isAuthenticated } = auth;

  if (isAuthenticated()) {
    return <button onclick={auth.logout}>Logout {auth.getUserName()}</button>;
  } else {
    return <button onclick={auth.login}>Login</button>;
  }
};

export default Login;
`}</code></pre>
    <p>{`Since Auth0 handles all our user state, we don't need any sort of props or state management. Just a component that instantiates an Auth0 object and renders a button.`}</p>
    <p>{`Use the `}<inlineCode parentName="p">{`isAuthenticated`}</inlineCode>{` helper to decide whether to say `}<inlineCode parentName="p">{`Login`}</inlineCode>{` or `}<inlineCode parentName="p">{`Logout`}</inlineCode>{` and call the appropriate methods on click.`}</p>
    <p>{`That's it.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Once I got Gatsby and Auth0 to play together it was really quite delightful. Short writeup coming soon `}<a parentName="p" {...{
          "href": "https://t.co/eBNFGmxmoq"
        }}>{`pic.twitter.com/eBNFGmxmoq`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1088117007138975744"
      }}>{`January 23, 2019`}</a></blockquote>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      